import { useLocations, useCreateLocation, useMatchLocationByCoordinates, MatchByCoordinatesResult } from '@/location/api'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Plus, Building2Icon, Loader2, MapPin, Sparkles } from 'lucide-react'
import { useState, useEffect } from 'react'
import { AddressAutofill } from '@mapbox/search-js-react'
import { Combobox } from '@/components/combo-box'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { locationFormSchema } from '@/installFlow/schemas'
import { US_STATES, getStateLabel } from '@/location/constants'
import { useAddressAutofill } from '@/location/useAddressAutofill'
import { StepFrame } from '@/components/installFlow/StepFrame'
import { StepHeader } from '@/components/installFlow/StepHeader'
import { StepFooter } from '@/components/installFlow/StepFooter'
import { useInstallFlowNavigation } from '@/installFlow/useInstallFlowNavigation'
import { useSelectedOrganizationId } from '@/organization/api'
import { useStepSearch } from '@/installFlow/useStepSearch'
import { useNameplateScan } from '@/installFlow/nameplateScan/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * Inner component that fetches and displays location list
 * Separated to isolate the suspending useLocations call
 */
function LocationList({
  organizationId,
  searchQuery,
  onSelect,
  matchedLocationId,
}: {
  organizationId: number
  searchQuery: string
  onSelect: (locationId: number, selectedLocation: any) => void
  matchedLocationId?: number | null
}) {
  const { data: locationsData } = useLocations({
    filters: { organization_id: organizationId },
    pageSize: 100,
  })

  // Filter locations based on search query
  const filteredLocations = locationsData.results.filter((location) =>
    location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.address_line_1.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.city.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Sort to move matched location to the top
  const sortedLocations = [...filteredLocations].sort((a, b) => {
    const aIsMatched = matchedLocationId && a.id === matchedLocationId
    const bIsMatched = matchedLocationId && b.id === matchedLocationId

    if (aIsMatched && !bIsMatched) return -1
    if (!aIsMatched && bIsMatched) return 1
    return 0
  })

  const handleSelectLocation = (locationId: number) => {
    const selectedLocation = locationsData.results.find(loc => loc.id === locationId)
    if (selectedLocation) {
      onSelect(locationId, selectedLocation)
    }
  }

  return (
    <div className="space-y-4">
      {sortedLocations.map((location) => {
        const isMatched = matchedLocationId && location.id === matchedLocationId

        return (
          <Card
            key={location.id}
            className={`p-4 cursor-pointer transition-colors ${
              isMatched
                ? 'border-2 border-thalo-blueLight hover:border-thalo-blueLight'
                : 'hover:border-primary'
            }`}
            onClick={() => handleSelectLocation(location.id)}
            data-testid={`location-card-${location.id}`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`aspect-square flex items-center justify-center p-2 rounded-lg ${
                  isMatched ? 'bg-thalo-blueLight' : 'bg-blue-50'
                }`}
              >
                <Building2Icon
                  className={`h-5 w-5 ${
                    isMatched ? 'text-white' : 'text-muted-foreground'
                  }`}
                />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">{location.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {location.address_line_1}
                  {location.address_line_2 && `, ${location.address_line_2}`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {location.city}, {location.state} {location.postal_code}
                </p>
                {isMatched && (
                  <div className="flex items-center gap-1 mt-2 text-sm font-medium text-thalo-blueLight">
                    <MapPin className="h-4 w-4" />
                    <span>
                      It looks like you're here
                    </span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )
      })}
    </div>
  )
}

export function LocationStep() {
  const { goToStep, goToPreviousStep } = useInstallFlowNavigation()
  const organizationId = useSelectedOrganizationId()
  const searchParams = useStepSearch('location')

  const createLocation = useCreateLocation()
  const [showForm, setShowForm] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Nameplate scan EXIF polling
  const scanId = searchParams.scanId
  const [showLoadingDialog, setShowLoadingDialog] = useState(!!scanId)

  // Start polling immediately if scanId exists
  const { data: scan } = useNameplateScan(scanId, {
    enabled: !!scanId,
  })

  // Browser geolocation state
  const [browserGeocodingResult, setBrowserGeocodingResult] = useState<MatchByCoordinatesResult | null>(null)
  const [showBrowserLocationDialog, setShowBrowserLocationDialog] = useState(false)
  const [browserLocationError, setBrowserLocationError] = useState<string | null>(null)
  const matchByCoordinates = useMatchLocationByCoordinates()

  // 5 second timeout - close dialog after 5s regardless of results
  useEffect(() => {
    if (!scanId) return

    const timeout = setTimeout(() => {
      setShowLoadingDialog(false)
    }, 5000)

    return () => clearTimeout(timeout)
  }, [scanId])

  // Close dialog immediately when EXIF data arrives (success or failure)
  useEffect(() => {
    if (!scan) return

    // EXIF data arrived or scan failed - close dialog
    if (scan.exif_result || scan.status === 'failed') {
      setShowLoadingDialog(false)
    }
  }, [scan])

  // Trigger browser geolocation AFTER EXIF completes with no GPS
  useEffect(() => {
    if (!scan?.exif_result) return

    // EXIF completed but has no GPS data - try browser geolocation
    if (!scan.exif_result.has_gps) {
      setShowBrowserLocationDialog(true)
      requestBrowserLocation()
    }
  }, [scan?.exif_result?.has_gps])

  const requestBrowserLocation = () => {
    if (!navigator.geolocation) {
      setBrowserLocationError('Geolocation not supported by your browser')
      return
    }

    navigator.geolocation.getCurrentPosition(
      // Success callback
      async (position) => {
        try {
          const result = await matchByCoordinates.mutateAsync({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
          setBrowserGeocodingResult(result)
          setShowBrowserLocationDialog(false)
        } catch (error) {
          // Neither match nor geocoding succeeded - that's okay, user can select manually
          setShowBrowserLocationDialog(false)
        }
      },
      // Error callback
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setBrowserLocationError('Location permission denied')
        } else if (error.code === error.TIMEOUT) {
          setBrowserLocationError('Location request timed out')
        } else {
          setBrowserLocationError('Unable to get your location')
        }
        setShowBrowserLocationDialog(false)
      },
      // Options
      {
        timeout: 10000, // 10 second timeout
        enableHighAccuracy: true,
      }
    )
  }

  const form = useForm<z.infer<typeof locationFormSchema>>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'USA',
    },
    mode: 'onChange',
  })

  const handleSelectLocation = (locationId: number, selectedLocation: any) => {
    goToStep('installation', {
      locationId: selectedLocation.id,
    })
  }

  const { handleAutofillRetrieve, coordinates } = useAddressAutofill(form)

  const handleCreateLocation = async (values: z.infer<typeof locationFormSchema>) => {
    try {
      // Create location on server (SAVED, not draft)
      const newLocation = await createLocation.mutateAsync({
        ...values,
        organization: organizationId,
        ...coordinates ?? {},
      })

      form.reset()
      setShowForm(false)
      goToStep('installation', {
        locationId: newLocation.id,
      })
    } catch (error) {
      console.error('Failed to create location:', error)
    }
  }

  const handleBack = () => {
    goToPreviousStep()
  }

  if (showForm) {
    return (
      <StepFrame
        header={
          <StepHeader
            title="Add new property"
            onBack={() => setShowForm(false)}
          />
        }
        main={
          <div className="space-y-4">
            <div className="flex flex-col space-y-2 items-center justify-center text-center">
              <div className="w-fit aspect-square rounded-lg bg-blue-100 p-4 flex justify-center items-center">
                <Building2Icon className="text-thalo-blueLight" size={48}/>
              </div>
              <div className="text-muted-foreground space-y-2">
                <p>Enter the property details to add it to your Thalo Copilot account.</p>
              </div>
            </div>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleCreateLocation)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <Label htmlFor="name">
                        Location Name <span className="text-red-500">*</span>
                      </Label>
                      <FormControl>
                        <Input
                          id="name"
                          placeholder="e.g. Head office, Carson's House"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address_line_1"
                  render={({ field }) => (
                    <FormItem>
                      <Label htmlFor="address_line_1">
                        Address Line 1 <span className="text-red-500">*</span>
                      </Label>
                      <FormControl>
                        {/* @ts-expect-error - Mapbox AddressAutofill has React 18 type compatibility issues */}
                        <AddressAutofill
                          accessToken={import.meta.env.VITE_MAPBOX_API_KEY}
                          onRetrieve={handleAutofillRetrieve}
                        >
                          <Input
                            id="address_line_1"
                            autoComplete="address-line1"
                            {...field}
                          />
                        </AddressAutofill>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address_line_2"
                  render={({ field }) => (
                    <FormItem>
                      <Label htmlFor="address_line_2">Address Line 2</Label>
                      <FormControl>
                        <Input
                          id="address_line_2"
                          autoComplete="address-line2"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <Label htmlFor="city">
                          City <span className="text-red-500">*</span>
                        </Label>
                        <FormControl>
                          <Input
                            id="city"
                            autoComplete="address-level2"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <Label htmlFor="state">
                          State <span className="text-red-500">*</span>
                        </Label>
                        <FormControl>
                          <Combobox
                            placeholder="Select state..."
                            options={US_STATES}
                            onSelect={field.onChange}
                            defaultLabel={getStateLabel(field.value)}
                            className="w-full"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="postal_code"
                  render={({ field }) => (
                    <FormItem>
                      <Label htmlFor="postal_code">
                        Postal Code <span className="text-red-500">*</span>
                      </Label>
                      <FormControl>
                        <Input
                          id="postal_code"
                          autoComplete="postal-code"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
        }
        footer={
          <StepFooter
            actions={[
              <Button
                key="save"
                onClick={form.handleSubmit(handleCreateLocation)}
                disabled={!form.formState.isValid || createLocation.isPending}
                className="w-full"
                size="lg"
              >
                {createLocation.isPending ? 'Saving...' : 'Save property'}
              </Button>,
            ]}
          />
        }
      />
    )
  }

  // Extract matched location data from EXIF result or browser geolocation
  // Prefer EXIF match, fallback to browser location match
  const matchedLocationId =
    scan?.exif_result?.matched_location_id ??
    browserGeocodingResult?.matched_location?.id ??
    null

  // Get reverse geocode address (from EXIF or browser)
  const reverseGeocodedAddress =
    scan?.exif_result?.reverse_geocode_address ??
    browserGeocodingResult?.reverse_geocode_address ??
    null

  const reverseGeocodedDisplay =
    scan?.exif_result?.reverse_geocode_display ??
    browserGeocodingResult?.reverse_geocode_display ??
    null

  return (
    <>
      <StepFrame
        header={
          <StepHeader
            title="Which property is this sensor at?"
            onBack={handleBack}
            searchBar={{
              placeholder: 'Search properties',
              value: searchQuery,
              onChange: (e) => setSearchQuery(e.target.value),
            }}
          />
        }
        main={
          <div className="space-y-4">
            {/* Show suggested location from reverse geocoding */}
            {reverseGeocodedAddress && !matchedLocationId && (
              <Card
                className="p-4 cursor-pointer transition-colors border-2 border-thalo-blueLight hover:border-thalo-blueLight"
                onClick={() => {
                  // Pre-fill the "Add new property" form with reverse geocoded data
                  setShowForm(true)
                  form.setValue('address_line_1', reverseGeocodedAddress.street)
                  form.setValue('city', reverseGeocodedAddress.city)
                  form.setValue('state', reverseGeocodedAddress.state)
                  form.setValue('postal_code', reverseGeocodedAddress.postal_code)
                  form.setValue('country', reverseGeocodedAddress.country)
                }}
                data-testid="suggested-location-card"
              >
                <div className="flex items-center gap-4">
                  <div className="aspect-square flex items-center justify-center p-2 rounded-lg bg-thalo-blueLight">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium">{reverseGeocodedAddress.street}</h3>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-thalo-blueLight text-white">
                        <Sparkles className="h-3 w-3" />
                        Suggested
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {reverseGeocodedDisplay}
                    </p>
                    <div className="flex items-center gap-1 mt-2 text-sm font-medium text-thalo-blueLight">
                      <span>Click to edit and add this property</span>
                    </div>
                  </div>
                </div>
              </Card>
            )}
            <LocationList
              organizationId={organizationId}
              searchQuery={searchQuery}
              onSelect={handleSelectLocation}
              matchedLocationId={matchedLocationId}
            />
          </div>
        }
        footer={
          <StepFooter
            actions={[
              <Button key="add-property" variant="outline" onClick={() => setShowForm(true)} className="w-full" size="lg">
                <Plus className="h-4 w-4 mr-2" />
                Add new property
              </Button>,
            ]}
          />
        }
      />

      {/* EXIF Loading Dialog */}
      <Dialog open={showLoadingDialog} onOpenChange={setShowLoadingDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Analyzing photo location</DialogTitle>
            <DialogDescription>
              We're extracting GPS data from your photo to help find the property...
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>

      {/* Browser Geolocation Dialog */}
      <Dialog open={showBrowserLocationDialog} onOpenChange={setShowBrowserLocationDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {browserLocationError ? 'Location unavailable' : 'Find property location'}
            </DialogTitle>
            <DialogDescription>
              {browserLocationError
                ? browserLocationError
                : "We'd like to use your device's location to help find the property. You can still select manually if you prefer."
              }
            </DialogDescription>
          </DialogHeader>
          {!browserLocationError && matchByCoordinates.isPending && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {browserLocationError && (
            <div className="flex justify-end">
              <Button onClick={() => setShowBrowserLocationDialog(false)}>
                Continue
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}